import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  getAuditLogs, 
  getAuditLogStats, 
  AuditLogFilters, 
  AuditLogEntry, 
  AuditLogStats,
  AuditLogResponse,
  AuditLogPagination
} from '../../services/api';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
// Import the actual Tabs component but rename it to avoid conflicts
import OriginalTabs from '../../components/ui/Tabs';
import { 
  Loader2, 
  RefreshCw, 
  Filter, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  Search, 
  Info,
  AlertCircle,
  Clock,
  User,
  Globe,
  FileText,
  ArrowUpDown,
  BarChart3,
  List,
  Download,
  Layers,
  Zap,
  Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Define custom types for component props
type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'default' | 'primary' | 'secondary';

// Create simple versions of the remaining UI components
const CardHeader: React.FC<React.PropsWithChildren<any>> = ({ children }) => (
  <div className="px-6 py-4 border-b border-border-dark bg-background-dark/50 backdrop-blur-sm rounded-t-lg shadow-sm">{children}</div>
);

const CardContent: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = "" }) => (
  <div className={`p-6 bg-background-dark/30 ${className}`}>{children}</div>
);

const CardFooter: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = "" }) => (
  <div className={`px-6 py-4 border-t border-border-dark bg-background-dark/50 backdrop-blur-sm rounded-b-lg shadow-sm ${className}`}>{children}</div>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => {
  const { className, ...rest } = props;
  return (
    <input
      {...rest}
      className={`px-3 py-2 border border-border-dark rounded-md bg-background-dark text-white w-full focus:outline-none focus:ring-2 focus:ring-primary ${className || ''}`}
    />
  );
};

// Simple Select components
const SelectTrigger: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = '' }) => (
  <div className={`px-3 py-2 border border-indigo-800/30 rounded-md bg-background-dark text-white flex justify-between items-center cursor-pointer hover:border-indigo-600/50 ${className}`}>
    {children}
    <ChevronDown className="h-4 w-4" />
  </div>
);

const SelectValue: React.FC<React.PropsWithChildren<{ placeholder?: string }>> = ({ children, placeholder }) => (
  <span>{children || placeholder}</span>
);

const SelectContent: React.FC<React.PropsWithChildren<any>> = ({ children }) => (
  <div className="mt-1 w-full rounded-md bg-background-dark border border-indigo-600/50 shadow-lg ring-1 ring-indigo-500/20 overflow-hidden">
    <div className="py-1 max-h-60 overflow-auto">{children}</div>
  </div>
);

const SelectItem: React.FC<React.PropsWithChildren<{ value: string }>> = ({ children, value }) => (
  <div className="px-3 py-2 hover:bg-indigo-800/50 cursor-pointer text-white hover:text-indigo-200" data-value={value}>
    {children}
  </div>
);

// Simple Select wrapper component
const Select: React.FC<{
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}> = ({ value, onValueChange, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [dropdownStyles, setDropdownStyles] = useState<{
    top?: string;
    bottom?: string;
    left: string;
    width: string;
    maxHeight: string;
  }>({
    top: '0px',
    left: '0px',
    width: '100%',
    maxHeight: '300px'
  });
  
  // Calculate dropdown position on open
  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const preferredHeight = 300; // max height for dropdown
      
      // Check if there's enough space below
      if (spaceBelow >= 100 || spaceBelow >= spaceAbove) {
        // Position below
        setDropdownStyles({
          top: `${rect.height}px`,
          bottom: undefined,
          left: '0px',
          width: '100%',
          maxHeight: `${Math.min(preferredHeight, spaceBelow - 10)}px`
        });
      } else {
        // Position above
        setDropdownStyles({
          top: undefined,
          bottom: `${rect.height}px`,
          left: '0px',
          width: '100%',
          maxHeight: `${Math.min(preferredHeight, spaceAbove - 10)}px`
        });
      }
    }
  }, [open]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const item = target.closest('[data-value]');
    if (item) {
      const selectedValue = item.getAttribute('data-value') || '';
      onValueChange(selectedValue);
      setOpen(false);
    }
  };
  
  // Find the selected item's text to display
  let selectedText = '';
  React.Children.forEach(children, child => {
    if (React.isValidElement(child) && child.type === SelectContent) {
      const contentChild = child as React.ReactElement<{children?: React.ReactNode}>;
      React.Children.forEach(contentChild.props.children, item => {
        if (React.isValidElement(item)) {
          const selectItem = item as React.ReactElement<{value?: string, children?: React.ReactNode}>;
          if (selectItem.props.value === value) {
            selectedText = String(selectItem.props.children || '');
          }
        }
      });
    }
  });
  
  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setOpen(!open)}>
        {React.Children.map(children, child => {
          if (React.isValidElement(child) && child.type === SelectTrigger) {
            const triggerChild = child as React.ReactElement<{children?: React.ReactNode}>;
            // Clone the trigger and inject the selected value
            return React.cloneElement(triggerChild, {}, 
              React.Children.map(triggerChild.props.children, innerChild => {
                if (React.isValidElement(innerChild) && innerChild.type === SelectValue) {
                  const valueChild = innerChild as React.ReactElement<{children?: React.ReactNode, placeholder?: string}>;
                  return React.cloneElement(valueChild, {}, 
                    selectedText || valueChild.props.children || valueChild.props.placeholder
                  );
                }
                return innerChild;
              })
            );
          }
          return null;
        })}
      </div>
      {open && (
        <div 
          onClick={handleClick} 
          className="absolute z-[1000] w-full shadow-lg"
          style={{
            ...dropdownStyles,
            position: 'fixed',
          }}
        >
          {React.Children.map(children, child => {
            if (React.isValidElement(child) && child.type === SelectContent) {
              return child;
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

// Simple Table components
const Table: React.FC<React.PropsWithChildren<any>> = ({ children }) => (
  <table className="w-full">{children}</table>
);

const TableHeader: React.FC<React.PropsWithChildren<any>> = ({ children }) => (
  <thead className="bg-background-dark/70 backdrop-blur-sm sticky top-0 z-10">{children}</thead>
);

const TableBody: React.FC<React.PropsWithChildren<any>> = ({ children }) => (
  <tbody className="divide-y divide-border-dark">{children}</tbody>
);

const TableRow: React.FC<React.PropsWithChildren<any>> = ({ children }) => (
  <tr className="hover:bg-background-hover transition-colors">{children}</tr>
);

const TableHead: React.FC<React.PropsWithChildren<any>> = ({ children }) => (
  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">{children}</th>
);

const TableCell: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-sm ${className}`}>{children}</td>
);

// Simple Tabs components
interface TabProps {
  id: string;
  label: string;
}

// Custom Tabs components for our implementation
const TabsList: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = "" }) => (
  <div className={`flex border-b border-border-dark ${className}`}>{children}</div>
);

const TabsTrigger: React.FC<React.PropsWithChildren<{ value: string; onClick?: () => void }>> = ({ children, value, onClick }) => (
  <button 
    className={`px-4 py-2 text-sm font-medium border-b-2 focus:outline-none ${
      value === 'active' ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-gray-200'
    }`}
    onClick={onClick}
  >
    {children}
  </button>
);

const TabsContent: React.FC<React.PropsWithChildren<{ value: string; className?: string }>> = ({ children, value, className = "" }) => (
  <div className={`py-4 ${className}`}>{children}</div>
);

// Format timestamp function without date-fns dependency
const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return timestamp;
    }
    
    // Convert to Indian time (UTC+5:30)
    const indianTime = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    
    // Format as YYYY-MM-DD HH:MM:SS
    const year = indianTime.getFullYear();
    const month = String(indianTime.getMonth() + 1).padStart(2, '0');
    const day = String(indianTime.getDate()).padStart(2, '0');
    const hours = String(indianTime.getHours()).padStart(2, '0');
    const minutes = String(indianTime.getMinutes()).padStart(2, '0');
    const seconds = String(indianTime.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} IST`;
  } catch (error) {
    return timestamp;
  }
};

// Format JSON with syntax highlighting and error highlighting
const formatJsonWithHighlighting = (json: any, isError: boolean = false): string => {
  try {
    // First convert to formatted JSON string
    const jsonString = JSON.stringify(json, null, 2);
    
    // Basic syntax highlighting
    let highlighted = jsonString
      .replace(/"([^"]+)":/g, '<span class="text-blue-400">"$1"</span>:')
      .replace(/: "([^"]+)"/g, ': <span class="text-green-400">"$1"</span>')
      .replace(/: (\d+)/g, ': <span class="text-amber-400">$1</span>')
      .replace(/: (true|false)/g, ': <span class="text-rose-400">$1</span>');
    
    // If it's an error response, add extra highlighting
    if (isError) {
      // Highlight error, message, details fields
      highlighted = highlighted
        .replace(/"(error|message|details)":/g, '<span class="text-rose-500 font-semibold">"$1"</span>:')
        .replace(/: "(.*?error.*?)"/gi, ': <span class="text-rose-400 font-semibold">"$1"</span>')
        .replace(/: "(.*?fail.*?)"/gi, ': <span class="text-rose-400 font-semibold">"$1"</span>')
        .replace(/: "(.*?invalid.*?)"/gi, ': <span class="text-rose-400 font-semibold">"$1"</span>')
        .replace(/: "(.*?exception.*?)"/gi, ': <span class="text-rose-400 font-semibold">"$1"</span>');
    }
    
    return highlighted;
  } catch (error) {
    // If anything goes wrong, return the original JSON stringified
    console.error('Error formatting JSON:', error);
    return JSON.stringify(json, null, 2);
  }
};

// Add IP geolocation types
interface IPGeolocation {
  country: string;
  city: string;
  region: string;
  org?: string;
  timezone?: string;
  loading: boolean;
  error?: string;
}

// Function to fetch location data from IP
const useIPGeolocation = () => {
  const [geoCache, setGeoCache] = useState<Record<string, IPGeolocation>>({});
  const [rateLimited, setRateLimited] = useState(false);
  const pendingRequests = useRef<Set<string>>(new Set());

  // For local/private IPs, don't try to fetch location data
  const isLocalIP = useCallback((ip: string): boolean => {
    return ip.startsWith('127.') || 
           ip.startsWith('10.') || 
           ip.startsWith('172.16.') || 
           ip.startsWith('192.168.') || 
           ip === 'localhost' || 
           ip === '::1';
  }, []);

  const getLocationData = useCallback(async (ip: string): Promise<IPGeolocation> => {
    // Return from cache if available
    if (geoCache[ip]) {
      return geoCache[ip];
    }

    // For local/private IPs, return mock data without API call
    if (isLocalIP(ip)) {
      const localData: IPGeolocation = {
        country: 'Local Network',
        city: 'Internal',
        region: 'Private IP',
        org: 'Your Organization',
        loading: false
      };
      
      setGeoCache(prev => ({
        ...prev,
        [ip]: localData
      }));
      
      return localData;
    }

    // If we're currently rate limited, return error state
    if (rateLimited) {
      const errorData: IPGeolocation = {
        country: 'Unknown',
        city: 'Unknown',
        region: '',
        loading: false,
        error: 'Rate limited, try again later'
      };
      
      setGeoCache(prev => ({
        ...prev,
        [ip]: errorData
      }));
      
      return errorData;
    }

    // If this IP is already being fetched, don't duplicate the request
    if (pendingRequests.current.has(ip)) {
      return {
        country: '',
        city: '',
        region: '',
        loading: true
      };
    }

    // Set loading state in cache
    setGeoCache(prev => ({
      ...prev,
      [ip]: { country: '', city: '', region: '', loading: true }
    }));
    
    // Add this IP to pending requests
    pendingRequests.current.add(ip);

    try {
      // Use ipgeolocation.io with an API key, or fallback to a CORS-friendly service
      // Or use a proxy on your own backend server to avoid CORS issues
      // For this example, we'll use a more reliable free service with JSONP to bypass CORS
      
      // Mock successful response for development (to avoid more API calls)
      const mockData = {
        country_name: `Country (${ip})`,
        city: `City (${ip.split('.').pop()})`,
        region: `Region ${ip.split('.')[2]}`,
        org: `ISP Organization ${ip.split('.')[1]}`,
        timezone: 'UTC+0',
      };
      
      // For production, implement one of these approaches:
      // 1. Create a backend proxy endpoint that fetches IP data server-side
      // 2. Use a service with JSONP support or proper CORS headers
      // 3. Use a paid API with higher rate limits and proper CORS

      const locationData: IPGeolocation = {
        country: mockData.country_name || 'Unknown',
        city: mockData.city || 'Unknown',
        region: mockData.region || '',
        org: mockData.org,
        timezone: mockData.timezone,
        loading: false
      };
      
      // Cache the result
      setGeoCache(prev => ({
        ...prev,
        [ip]: locationData
      }));
      
      // Remove from pending requests
      pendingRequests.current.delete(ip);
      
      return locationData;
    } catch (error) {
      console.error('Error fetching IP location:', error);
      
      // Check if we hit rate limits
      if (error instanceof Error && error.message.includes('429')) {
        setRateLimited(true);
        // Reset rate limited status after 1 minute
        setTimeout(() => setRateLimited(false), 60000);
      }
      
      // Cache the error state
      const errorData: IPGeolocation = {
        country: 'Unknown',
        city: 'Unknown',
        region: '',
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      
      setGeoCache(prev => ({
        ...prev,
        [ip]: errorData
      }));
      
      // Remove from pending requests
      pendingRequests.current.delete(ip);
      
      return errorData;
    }
  }, [geoCache, rateLimited, isLocalIP]);

  return { getLocationData, geoCache };
};

// Additional custom components for improved audit log UI
const AuditLogTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'api_call':
      return <Zap className="h-4 w-4 text-blue-400" />;
    case 'user_login':
      return <User className="h-4 w-4 text-green-400" />;
    case 'user_logout':
      return <User className="h-4 w-4 text-yellow-400" />;
    default:
      return <FileText className="h-4 w-4 text-gray-400" />;
  }
};

const MethodBadge: React.FC<{ method: string }> = ({ method }) => {
  let color = 'bg-gray-700 text-gray-300';
  
  switch (method?.toUpperCase()) {
    case 'GET':
      color = 'bg-blue-900/60 text-blue-300';
      break;
    case 'POST':
      color = 'bg-green-900/60 text-green-300';
      break;
    case 'PUT':
      color = 'bg-yellow-900/60 text-yellow-300';
      break;
    case 'PATCH':
      color = 'bg-purple-900/60 text-purple-300';
      break;
    case 'DELETE':
      color = 'bg-rose-900/60 text-rose-300';
      break;
  }
  
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${color}`}>
      {method?.toUpperCase() || 'N/A'}
    </span>
  );
};

const AuditLogPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('logs');
  const [activeDetailTab, setActiveDetailTab] = useState<string>('details');
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<AuditLogFilters>({ time_range: 'last_24h' });
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pagination, setPagination] = useState<AuditLogPagination>({ page: 1, per_page: 10, total: 0, pages: 0 });
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [statsTimeRange, setStatsTimeRange] = useState<'last_24h' | 'last_7d' | 'last_30d' | 'custom'>('last_24h');
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [ipDetailsOpen, setIpDetailsOpen] = useState<boolean>(false);
  const [selectedIp, setSelectedIp] = useState<string | null>(null);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [extractedValue, setExtractedValue] = useState<any>(null);
  const [responseModalOpen, setResponseModalOpen] = useState<boolean>(false);
  
  // Get geolocation data
  const { getLocationData, geoCache } = useIPGeolocation();
  
  // Fetch logs based on filters
  const fetchLogs = async () => {
    setLoading(true);
    
    try {
      // Make sure we include request and response data in the API call
      const apiFilters = {
        ...filters,
        include_data: true,
      };
      
      const response = await getAuditLogs(apiFilters);
      // Show all audit logs so list and statistics stay consistent.
      setLogs(response.logs);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch stats
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const data = await getAuditLogStats(statsTimeRange);
      setStats(data);
    } catch (error) {
      toast.error('Failed to fetch audit log statistics');
      console.error('Error fetching audit log stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    fetchLogs();
  }, [filters.page, filters.per_page]);
  
  useEffect(() => {
    if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab, statsTimeRange]);
  
  // Apply search and filters
  const applySearch = () => {
    if (searchTerm) {
      // Update filters with the search term (endpoint is a good field to search)
      setFilters({
        ...filters,
        endpoint: searchTerm,
        page: 1 // Reset to first page when applying new search
      });
    } else {
      // If search term is empty, remove endpoint filter
      const { endpoint, ...restFilters } = filters;
      setFilters({
        ...restFilters,
        page: 1
      });
    }
    fetchLogs();
  };
  
  // Apply filters
  const applyFilters = () => {
    setFilters({
      ...filters,
      page: 1 // Reset to first page when applying new filters
    });
    fetchLogs();
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      time_range: 'last_24h',
      page: 1,
      per_page: 10,
      start_date: undefined,
      end_date: undefined
    });
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setShowFilters(false);
    fetchLogs();
  };
  
  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.pages) return;
    setFilters({
      ...filters,
      page: newPage
    });
  };
  
  // Get status badge color based on status code
  const getStatusBadgeColor = (statusCode: number | undefined): BadgeVariant => {
    if (!statusCode) return 'default';
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 300 && statusCode < 400) return 'warning';
    if (statusCode >= 400) return 'danger';
    return 'default';
  };
  
  // Define tabs for the Tabs component
  const tabItems: TabProps[] = [
    { id: 'logs', label: 'Logs' },
    { id: 'stats', label: 'Statistics' }
  ];
  
  // Handle tab change
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };
  
  // Add this function inside the component
  const openIPDetails = (ip: string) => {
    setSelectedIp(ip);
    setIpDetailsOpen(true);
    
    // Ensure we have location data for this IP
    if (!geoCache[ip] || geoCache[ip].error) {
      getLocationData(ip);
    }
  };

  // Add this function inside the component
  const closeIPDetails = () => {
    setIpDetailsOpen(false);
  };
  
  // Add these functions to the component
  const openLogDetails = (log: AuditLogEntry) => {
    setSelectedLog(log);
    setDetailsOpen(true);
    setActiveDetailTab('details');
    
    // If we have an IP address, ensure we fetch location data
    if (log.ip_address && (!geoCache[log.ip_address] || geoCache[log.ip_address].error)) {
      getLocationData(log.ip_address);
    }
  };

  const closeLogDetails = () => {
    setDetailsOpen(false);
    setSelectedField(null);
    setExtractedValue(null);
  };
  
  // Function to quickly view API response when clicking on status code
  const openResponseModal = (log: AuditLogEntry, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click event
    setSelectedLog(log);
    setResponseModalOpen(true);
  };
  
  // Close response modal
  const closeResponseModal = () => {
    setResponseModalOpen(false);
  };
  
  // Export logs to CSV
  const exportToCSV = () => {
    try {
      // Create CSV content
      let csvContent = "data:text/csv;charset=utf-8,";
      
      // Add headers
      csvContent += "Timestamp,Event Type,User,IP Address,Endpoint,Method,Status,Response Time\n";
      
      // Add data rows
      logs.forEach(log => {
        const row = [
          log.timestamp,
          log.event_type,
          log.username || 'N/A',
          log.ip_address || 'N/A',
          log.endpoint || 'N/A',
          log.method || 'N/A',
          log.status_code || 'N/A',
          log.response_time || 'N/A'
        ];
        
        // Escape any commas in the data
        const escapedRow = row.map(field => {
          const str = String(field);
          return str.includes(',') ? `"${str}"` : str;
        });
        
        csvContent += escapedRow.join(',') + "\n";
      });
      
      // Create download link
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      document.body.removeChild(link);
      
      toast.success('Audit logs exported successfully');
    } catch (error) {
      console.error('Error exporting logs:', error);
      toast.error('Failed to export audit logs');
    }
  };
  
  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header section with gradient background */}
      <div className="mb-6 rounded-lg overflow-hidden relative z-10">
        <div className="bg-gradient-to-b from-gray-900/90 via-indigo-900/20 to-transparent p-6 rounded-xl backdrop-blur-sm border border-indigo-800/20 shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">Audit Logs</h1>
              <p className="text-gray-400 mt-1">
                Monitor and analyze system activity and security events
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowFilters(!showFilters)}
                className="bg-background-dark/50 backdrop-blur-sm border-indigo-800/30 hover:bg-background-dark/70"
              >
                {showFilters ? <X className="h-4 w-4 mr-2" /> : <Filter className="h-4 w-4 mr-2" />}
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => activeTab === 'logs' ? fetchLogs() : fetchStats()}
                disabled={loading || statsLoading}
                className="bg-background-dark/50 backdrop-blur-sm border-indigo-800/30 hover:bg-background-dark/70"
              >
                {(loading || statsLoading) ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </Button>
              {activeTab === 'logs' && logs.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={exportToCSV}
                  className="bg-background-dark/50 backdrop-blur-sm border-indigo-800/30 hover:bg-background-dark/70"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              )}
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="mt-4">
            <div className="relative">
              <input 
                type="text" 
                placeholder="Search by endpoint, username, or event type..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applySearch();
                  }
                }}
                className="w-full px-4 py-2 pl-10 pr-14 bg-background-dark/60 border border-indigo-800/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <button 
                onClick={applySearch}
                className="absolute right-3 top-1.5 px-2 py-1 bg-indigo-600/70 hover:bg-indigo-600 rounded text-xs font-medium text-white transition-colors"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value={activeTab === 'logs' ? 'active' : ''} onClick={() => setActiveTab('logs')}>
            <List className="h-4 w-4 mr-2" />
            Logs
          </TabsTrigger>
          <TabsTrigger value={activeTab === 'stats' ? 'active' : ''} onClick={() => setActiveTab('stats')}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Statistics
          </TabsTrigger>
        </TabsList>
        
        {activeTab === 'logs' && (
          <TabsContent value="active" className="space-y-4">
            {showFilters && (
              <Card className="bg-background-dark/40 backdrop-blur-sm border border-indigo-900/50 rounded-xl shadow-2xl overflow-visible relative z-50 transition-all duration-300 mb-6">
                <CardHeader className="bg-gradient-to-r from-indigo-900/40 to-violet-900/40 border-b border-indigo-800/40 py-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium flex items-center text-white">
                      <Filter className="h-5 w-5 mr-2 text-indigo-300" />
                      <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-200 to-violet-200 font-semibold">Advanced Filters</span>
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setFilters({});
                          setStartDate('');
                          setEndDate('');
                        }}
                        className="border-indigo-700/40 hover:border-indigo-600/60 hover:bg-indigo-900/30 text-indigo-200 hover:text-indigo-100 transition-all h-8 px-3"
                        size="sm"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Reset
                      </Button>
                      <Button 
                        variant="primary" 
                        onClick={() => {
                          setFilters(prev => ({ ...prev, page: 1 }));
                          fetchLogs();
                        }}
                        className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 border-none text-white shadow-lg hover:shadow-xl transition-all h-8 px-3"
                        size="sm"
                      >
                        <Search className="h-3 w-3 mr-1" />
                        Apply
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6 bg-gradient-to-b from-background-dark/60 to-background-dark/40">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
                    {/* First Row - Main Filters */}
                    <div className="space-y-3 bg-indigo-900/20 p-4 rounded-lg border border-indigo-700/40 shadow-inner transition-all hover:border-indigo-600/50 hover:bg-indigo-900/30">
                      <label className="text-sm font-medium flex items-center text-indigo-100">
                        <Layers className="h-4 w-4 mr-2 text-indigo-300" />
                        Event Type
                      </label>
                      <Select 
                        value={filters.event_type || ''} 
                        onValueChange={(value: string) => setFilters({...filters, event_type: value || undefined})}
                      >
                        <SelectTrigger className="bg-background-dark/80 border-indigo-700/40 hover:border-indigo-600/60 focus:ring-1 focus:ring-indigo-500/50 transition-all">
                          <SelectValue placeholder="All Events" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Events</SelectItem>
                          <SelectItem value="api_call">API Call</SelectItem>
                          <SelectItem value="user_login">User Login</SelectItem>
                          <SelectItem value="user_logout">User Logout</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-3 bg-indigo-900/20 p-4 rounded-lg border border-indigo-700/40 shadow-inner transition-all hover:border-indigo-600/50 hover:bg-indigo-900/30">
                      <label className="text-sm font-medium flex items-center text-indigo-100">
                        <User className="h-4 w-4 mr-2 text-indigo-300" />
                        Username
                      </label>
                      <Input 
                        placeholder="Filter by username" 
                        value={filters.username || ''} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({...filters, username: e.target.value || undefined})}
                        className="bg-background-dark/80 border-indigo-700/40 hover:border-indigo-600/60 focus:ring-1 focus:ring-indigo-500/50 transition-all"
                      />
                    </div>
                    
                    <div className="space-y-3 bg-indigo-900/20 p-4 rounded-lg border border-indigo-700/40 shadow-inner transition-all hover:border-indigo-600/50 hover:bg-indigo-900/30">
                      <label className="text-sm font-medium flex items-center text-indigo-100">
                        <FileText className="h-4 w-4 mr-2 text-indigo-300" />
                        Endpoint
                      </label>
                      <Input 
                        placeholder="Filter by endpoint" 
                        value={filters.endpoint || ''} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({...filters, endpoint: e.target.value || undefined})}
                        className="bg-background-dark/80 border-indigo-700/40 hover:border-indigo-600/60 focus:ring-1 focus:ring-indigo-500/50 transition-all"
                      />
                    </div>
                    
                    {/* Second Row - Additional Filters */}
                    <div className="space-y-3 bg-indigo-900/20 p-4 rounded-lg border border-indigo-700/40 shadow-inner transition-all hover:border-indigo-600/50 hover:bg-indigo-900/30">
                      <label className="text-sm font-medium flex items-center text-indigo-100">
                        <ArrowUpDown className="h-4 w-4 mr-2 text-indigo-300" />
                        Method
                      </label>
                      <Select 
                        value={filters.method || ''} 
                        onValueChange={(value: string) => setFilters({...filters, method: value || undefined})}
                      >
                        <SelectTrigger className="bg-background-dark/80 border-indigo-700/40 hover:border-indigo-600/60 focus:ring-1 focus:ring-indigo-500/50 transition-all">
                          <SelectValue placeholder="All Methods" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All Methods</SelectItem>
                          <SelectItem value="GET">GET</SelectItem>
                          <SelectItem value="POST">POST</SelectItem>
                          <SelectItem value="PUT">PUT</SelectItem>
                          <SelectItem value="PATCH">PATCH</SelectItem>
                          <SelectItem value="DELETE">DELETE</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-3 bg-indigo-900/20 p-4 rounded-lg border border-indigo-700/40 shadow-inner transition-all hover:border-indigo-600/50 hover:bg-indigo-900/30">
                      <label className="text-sm font-medium flex items-center text-indigo-100">
                        <AlertCircle className="h-4 w-4 mr-2 text-indigo-300" />
                        Status Code
                      </label>
                      <Input 
                        placeholder="e.g. 200, 404, 500" 
                        value={filters.status_code?.toString() || ''} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value ? parseInt(e.target.value) : undefined;
                          setFilters({...filters, status_code: value});
                        }}
                        className="bg-background-dark/80 border-indigo-700/40 hover:border-indigo-600/60 focus:ring-1 focus:ring-indigo-500/50 transition-all"
                      />
                    </div>
                    
                    <div className="space-y-3 bg-indigo-900/20 p-4 rounded-lg border border-indigo-700/40 shadow-inner transition-all hover:border-indigo-600/50 hover:bg-indigo-900/30">
                      <label className="text-sm font-medium flex items-center text-indigo-100">
                        <Clock className="h-4 w-4 mr-2 text-indigo-300" />
                        Time Range
                      </label>
                      <Select 
                        value={filters.time_range || (!filters.time_range && (filters.start_date || filters.end_date) ? 'custom' : 'last_24h')} 
                        onValueChange={(value: string) => {
                          if (value === 'last_24h' || value === 'last_7d' || value === 'last_30d') {
                            // Clear custom date range when selecting a preset
                            setFilters({...filters, time_range: value, start_date: undefined, end_date: undefined});
                            setStartDate('');
                            setEndDate('');
                          } else if (value === 'custom') {
                            // Set time_range to 'custom' to trigger the display of date pickers
                            setFilters({...filters, time_range: 'custom'});
                          }
                        }}
                      >
                        <SelectTrigger className="bg-background-dark/80 border-indigo-700/40 hover:border-indigo-600/60 focus:ring-1 focus:ring-indigo-500/50 transition-all">
                          <SelectValue placeholder="Select Time Range">
                            {!filters.time_range && (filters.start_date || filters.end_date) ? 'Custom Range' : null}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="last_24h">Last 24 Hours</SelectItem>
                          <SelectItem value="last_7d">Last 7 Days</SelectItem>
                          <SelectItem value="last_30d">Last 30 Days</SelectItem>
                          <SelectItem value="custom">Custom Range</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Custom Date Range */}
                  {(!filters.time_range || filters.time_range === 'custom') && (
                    <div className="mt-6 p-5 border border-indigo-600/40 rounded-lg bg-gradient-to-r from-indigo-900/30 to-violet-900/30 backdrop-blur-sm shadow-lg relative z-30 transition-all">
                      <h4 className="text-sm font-medium text-indigo-100 mb-4 flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-indigo-300" />
                        Custom Date Range
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3 bg-indigo-950/30 p-4 rounded-lg border border-indigo-700/30 shadow-inner">
                          <label className="text-sm font-medium flex items-center text-indigo-100">
                            <Clock className="h-4 w-4 mr-2 text-indigo-300" />
                            Start Date
                          </label>
                          <Input 
                            type="datetime-local" 
                            value={startDate} 
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setStartDate(e.target.value);
                              if (e.target.value) {
                                setFilters({...filters, time_range: 'custom', start_date: new Date(e.target.value).toISOString()});
                              } else {
                                const { start_date, ...rest } = filters;
                                setFilters(rest);
                              }
                            }}
                            className="bg-background-dark/80 border-indigo-700/40 hover:border-indigo-600/60 focus:ring-1 focus:ring-indigo-500/50 transition-all"
                          />
                        </div>
                        <div className="space-y-3 bg-indigo-950/30 p-4 rounded-lg border border-indigo-700/30 shadow-inner">
                          <label className="text-sm font-medium flex items-center text-indigo-100">
                            <Clock className="h-4 w-4 mr-2 text-indigo-300" />
                            End Date
                          </label>
                          <Input 
                            type="datetime-local" 
                            value={endDate} 
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setEndDate(e.target.value);
                              if (e.target.value) {
                                setFilters({...filters, time_range: 'custom', end_date: new Date(e.target.value).toISOString()});
                              } else {
                                const { end_date, ...rest } = filters;
                                setFilters(rest);
                              }
                            }}
                            className="bg-background-dark/80 border-indigo-700/40 hover:border-indigo-600/60 focus:ring-1 focus:ring-indigo-500/50 transition-all"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
                

              </Card>
            )}
            
            <Card className="bg-background-dark/40 backdrop-blur-sm border border-border-dark/50 rounded-lg shadow-lg overflow-hidden relative z-20">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center items-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center p-8 flex flex-col items-center justify-center text-muted-foreground">
                    <FileText className="h-12 w-12 text-gray-600 mb-4" />
                    <p className="text-lg text-gray-400">No audit logs found matching the current filters.</p>
                    <p className="text-sm text-gray-500 mt-2">Try adjusting your search or filter criteria.</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={resetFilters} 
                      className="mt-4 bg-background-dark/50 border-indigo-800/30 hover:bg-background-dark/70"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset Filters
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gradient-to-r from-indigo-900/30 to-violet-900/30 border-b border-indigo-800/30">
                          <TableHead>Timestamp</TableHead>
                          <TableHead>Event Type</TableHead>
                          <TableHead>Activity</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>IP Address</TableHead>
                          <TableHead>Endpoint</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Response Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {logs.map((log) => (
                          <TableRow 
                            key={log.id} 
                            className="hover:bg-background-hover/40 transition-colors cursor-pointer" 
                            onClick={() => openLogDetails(log)}
                          >
                            <TableCell>
                              <div className="flex items-center">
                                <Clock className="h-3.5 w-3.5 mr-2 text-gray-400" />
                                <span className="text-xs whitespace-nowrap">{formatTimestamp(log.timestamp)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <AuditLogTypeIcon type={log.event_type} />
                                <Badge variant="primary" className="ml-2">
                                  {log.event_type}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs text-gray-300">
                                {log.activity_description
                                  ? log.activity_description
                                  : log.action
                                    ? log.action
                                    : `${(log.method || '').toUpperCase()} ${log.endpoint || ''}`}
                              </span>
                            </TableCell>
                            <TableCell>
                              {log.username ? (
                                <div className="flex items-center">
                                  <div className="w-6 h-6 rounded-full bg-indigo-800/60 flex items-center justify-center text-xs font-medium mr-2">
                                    {log.username[0].toUpperCase()}
                                  </div>
                                  <span>{log.username}</span>
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {log.ip_address ? (
                                <div className="flex flex-col">
                                  <span className="flex items-center">
                                    <span className="inline-block h-2 w-2 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
                                    <span className="font-mono text-xs">{log.ip_address}</span>
                                    <button 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (log.ip_address) {
                                          openIPDetails(log.ip_address);
                                        }
                                      }}
                                      className="ml-2 p-1 rounded-full hover:bg-indigo-900/40 text-gray-400 hover:text-indigo-300 transition-colors"
                                      title="View IP details"
                                    >
                                      <Info className="h-3 w-3" />
                                    </button>
                                  </span>
                                  {(() => {
                                    // Don't fetch data for private IPs
                                    if (log.ip_address && log.ip_address.startsWith('127.')) {
                                      return (
                                        <span className="text-xs text-gray-400 mt-1 flex items-center">
                                          <svg className="w-3 h-3 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                          </svg>
                                          Local
                                        </span>
                                      );
                                    }
                                    
                                    // Fetch location data when rendering (only if not already fetching/fetched)
                                    if (log.ip_address && !geoCache[log.ip_address]) {
                                      getLocationData(log.ip_address);
                                    }
                                    
                                    const ipData = geoCache[log.ip_address];
                                    
                                    if (!ipData) {
                                      return null;
                                    }
                                    
                                    if (ipData.loading) {
                                      return (
                                        <span className="text-xs text-gray-400 mt-1 flex items-center">
                                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                          Loading...
                                        </span>
                                      );
                                    }
                                    
                                    if (ipData.error) {
                                      return (
                                        <span className="text-xs text-gray-400 mt-1 flex items-center">
                                          <AlertCircle className="w-3 h-3 mr-1 text-rose-400" />
                                          Failed to get location
                                        </span>
                                      );
                                    }
                                    
                                    return (
                                      <span className="text-xs text-gray-400 mt-1 flex items-center">
                                        <Globe className="w-3 h-3 mr-1 text-blue-400" />
                                        {ipData.city && ipData.country ? 
                                          `${ipData.city}, ${ipData.country}` : 
                                          ipData.country || 'Unknown location'}
                                      </span>
                                    );
                                  })()}
                                </div>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="max-w-[200px] truncate" title={log.endpoint || ''}>
                                {log.endpoint || '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <MethodBadge method={log.method || ''} />
                            </TableCell>
                            <TableCell>
                              <div 
                                className="cursor-pointer hover:brightness-110 transition-all inline-block"
                                onClick={(e: React.MouseEvent) => openResponseModal(log, e)}
                              >
                                <Badge variant={getStatusBadgeColor(log.status_code)}>
                                  {log.status_code || 'N/A'}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              {log.response_time ? (
                                <div className="flex items-center">
                                  <span className="text-sm">{log.response_time}</span>
                                  <span className="text-xs text-gray-400 ml-1">ms</span>
                                </div>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
              {!loading && logs.length > 0 && (
                <CardFooter className="flex justify-between items-center bg-gradient-to-r from-indigo-900/10 to-violet-900/10 border-t border-indigo-800/30">
                  <div className="text-sm text-gray-400">
                    Showing {(pagination.page - 1) * pagination.per_page + 1}-
                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} logs
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handlePageChange(pagination.page - 1)} 
                      disabled={pagination.page <= 1}
                      className="bg-background-dark/50 border-indigo-800/30 hover:bg-background-dark/70 h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">Page {pagination.page} of {pagination.pages}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handlePageChange(pagination.page + 1)} 
                      disabled={pagination.page >= pagination.pages}
                      className="bg-background-dark/50 border-indigo-800/30 hover:bg-background-dark/70 h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardFooter>
              )}
            </Card>
          </TabsContent>
        )}
        
        {activeTab === 'stats' && (
          <TabsContent value="active" className="space-y-4">
            <Card className="bg-background-dark/40 backdrop-blur-sm border border-border-dark/50 rounded-lg shadow-lg overflow-hidden relative z-20">
              <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-indigo-900/30 to-violet-900/30 border-b border-indigo-800/30">
                <h3 className="text-lg font-semibold">Audit Log Statistics</h3>
                <Select 
                  value={statsTimeRange} 
                  onValueChange={(value: string) => {
                    if (value === 'last_24h' || value === 'last_7d' || value === 'last_30d') {
                      setStatsTimeRange(value);
                    }
                  }}
                >
                  <SelectTrigger className="w-[180px] bg-background-dark/60 border-indigo-800/30">
                    <SelectValue placeholder="Select Time Range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_24h">Last 24 Hours</SelectItem>
                    <SelectItem value="last_7d">Last 7 Days</SelectItem>
                    <SelectItem value="last_30d">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <div className="flex justify-center items-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : stats ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gradient-to-br from-indigo-900/30 to-violet-900/20 rounded-lg p-4 shadow-md border border-indigo-800/30 backdrop-blur-sm">
                          <div className="text-sm text-indigo-300/80 mb-1">Total Logs</div>
                          <div className="text-3xl font-bold text-white">{stats.total_logs.toLocaleString()}</div>
                          <div className="mt-2 text-xs text-indigo-300/60">
                            {statsTimeRange === 'last_24h' ? 'Past 24 hours' : 
                             statsTimeRange === 'last_7d' ? 'Past 7 days' : 'Past 30 days'}
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-rose-900/30 to-red-900/20 rounded-lg p-4 shadow-md border border-red-800/30 backdrop-blur-sm">
                          <div className="text-sm text-rose-300/80 mb-1">Error Rate</div>
                          <div className="text-3xl font-bold text-white">
                            {stats.total_logs ? 
                              `${Math.round((stats.error_count / stats.total_logs) * 100)}%` : 
                              '0%'}
                          </div>
                          <div className="mt-2 text-xs text-rose-300/60">
                            {stats.error_count.toLocaleString()} errors detected
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/20 rounded-lg p-4 shadow-md border border-emerald-800/30 backdrop-blur-sm">
                          <div className="text-sm text-emerald-300/80 mb-1">Avg Response Time</div>
                          <div className="text-3xl font-bold text-white">{stats.average_response_time}ms</div>
                          <div className="mt-2 text-xs text-emerald-300/60">
                            Server performance
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-amber-900/30 to-yellow-900/20 rounded-lg p-4 shadow-md border border-amber-800/30 backdrop-blur-sm">
                          <div className="text-sm text-amber-300/80 mb-1">Unique Users</div>
                          <div className="text-3xl font-bold text-white">{stats.top_users.length}</div>
                          <div className="mt-2 text-xs text-amber-300/60">
                            Active system users
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-background-dark/50 rounded-lg p-5 shadow-md border border-border-dark/50 backdrop-blur-sm">
                        <h4 className="font-medium mb-4 text-gray-300">Event Types</h4>
                        <div className="space-y-3">
                          {stats.event_types.map((item, index) => (
                            <div key={index} className="flex flex-col">
                              <div className="flex justify-between items-center mb-1">
                                <div className="flex items-center">
                                  <Badge variant="primary" className="mr-2">{item.type}</Badge>
                                  <span className="text-sm">{item.count.toLocaleString()}</span>
                                </div>
                                <span className="text-xs text-gray-400">
                                  {Math.round((item.count / stats.total_logs) * 100)}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-700/20 rounded-full h-1.5">
                                <div 
                                  className="bg-indigo-500 h-1.5 rounded-full" 
                                  style={{ 
                                    width: `${Math.min(100, (item.count / stats.total_logs) * 100)}%` 
                                  }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      <div className="bg-background-dark/50 rounded-lg p-5 shadow-md border border-border-dark/50 backdrop-blur-sm">
                        <h4 className="font-medium mb-4 text-gray-300">Status Codes</h4>
                        <div className="space-y-3">
                          {stats.status_codes.map((item, index) => (
                            <div key={index} className="flex flex-col">
                              <div className="flex justify-between items-center mb-1">
                                <div className="flex items-center">
                                  <Badge 
                                    variant={getStatusBadgeColor(item.code) as BadgeVariant}
                                    className="mr-2"
                                  >
                                    {item.code}
                                  </Badge>
                                  <span className="text-sm">{item.count.toLocaleString()}</span>
                                </div>
                                <span className="text-xs text-gray-400">
                                  {Math.round((item.count / stats.total_logs) * 100)}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-700/20 rounded-full h-1.5">
                                <div 
                                  className={`h-1.5 rounded-full ${
                                    item.code >= 400 ? 'bg-red-500' : 
                                    item.code >= 300 ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`}
                                  style={{ 
                                    width: `${Math.min(100, (item.count / stats.total_logs) * 100)}%` 
                                  }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-6">
                        <div className="bg-background-dark/50 rounded-lg p-5 shadow-md border border-border-dark/50 backdrop-blur-sm">
                          <h4 className="font-medium mb-3 text-gray-300">Top Endpoints</h4>
                          <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2 scrollbar-thin">
                            {stats.top_endpoints.map((item, index) => (
                              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-700/30 last:border-0">
                                <div className="flex items-center max-w-[70%]">
                                  <span className="text-sm truncate" title={item.endpoint}>
                                    {item.endpoint || 'Unknown'}
                                  </span>
                                </div>
                                <Badge variant="primary">{item.count}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="bg-background-dark/50 rounded-lg p-5 shadow-md border border-border-dark/50 backdrop-blur-sm">
                          <h4 className="font-medium mb-3 text-gray-300">Top Users</h4>
                          <div className="space-y-2">
                            {stats.top_users.map((item, index) => (
                              <div key={index} className="flex justify-between items-center py-1 border-b border-gray-700/30 last:border-0">
                                <div className="flex items-center">
                                  <div className="w-7 h-7 rounded-full bg-indigo-800/60 flex items-center justify-center text-xs font-medium mr-2">
                                    {(item.username || 'U')[0].toUpperCase()}
                                  </div>
                                  <span className="text-sm">{item.username || 'Unknown'}</span>
                                </div>
                                <Badge variant="primary">{item.count}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-8 text-muted-foreground">
                    No statistics available.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </div>
      
      {ipDetailsOpen && selectedIp && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-background-dark border border-border-dark/50 rounded-lg shadow-lg max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between bg-gradient-to-r from-indigo-900/30 to-violet-900/30 border-b border-indigo-800/30 p-4">
              <h3 className="text-lg font-medium flex items-center">
                <Globe className="h-5 w-5 mr-2 text-blue-400" />
                IP Address Details
              </h3>
              <button 
                onClick={closeIPDetails}
                className="p-1 rounded-full hover:bg-background-dark/40 text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-4 flex items-center">
                <span className="inline-block h-3 w-3 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
                <span className="font-mono text-lg">{selectedIp}</span>
              </div>
              
              {(() => {
                const ipData = geoCache[selectedIp];
                
                if (!ipData || ipData.loading) {
                  return (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                      <p className="text-gray-400">Loading IP information...</p>
                    </div>
                  );
                }
                
                if (ipData.error) {
                  return (
                    <div className="bg-rose-900/20 border border-rose-800/40 rounded-lg p-4 mb-4">
                      <p className="text-rose-300 flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2 text-rose-400" />
                        Error loading IP information
                      </p>
                      <p className="text-gray-400 mt-2 text-sm">{ipData.error}</p>
                      <button
                        onClick={() => getLocationData(selectedIp)}
                        className="mt-3 px-3 py-1 bg-rose-900/40 hover:bg-rose-800/40 text-rose-300 rounded-md text-sm flex items-center"
                      >
                        <RefreshCw className="h-3 w-3 mr-2" />
                        Try Again
                      </button>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    <div className="bg-indigo-900/20 border border-indigo-800/40 rounded-lg p-4">
                      <h4 className="text-indigo-300 font-medium mb-2 flex items-center">
                        <Globe className="h-4 w-4 mr-2" />
                        Location Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-400">Country</p>
                          <p className="text-sm flex items-center">
                            {ipData.country ? (
                              <>
                                <span className="w-5 h-3 bg-blue-500 rounded-sm mr-2 flex-shrink-0"></span>
                                {ipData.country}
                              </>
                            ) : 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">City</p>
                          <p className="text-sm">{ipData.city || 'Unknown'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Region</p>
                          <p className="text-sm">{ipData.region || 'Unknown'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Timezone</p>
                          <p className="text-sm">{ipData.timezone || 'Unknown'}</p>
                        </div>
                      </div>
                    </div>
                    
                    {ipData.org && (
                      <div className="bg-emerald-900/20 border border-emerald-800/40 rounded-lg p-4">
                        <h4 className="text-emerald-300 font-medium mb-2 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          Network Information
                        </h4>
                        <div>
                          <p className="text-xs text-gray-400">Organization</p>
                          <p className="text-sm">{ipData.org}</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={() => getLocationData(selectedIp)}
                        className="px-3 py-1 bg-indigo-900/40 hover:bg-indigo-800/40 text-indigo-300 rounded-md text-sm flex items-center mr-2"
                      >
                        <RefreshCw className="h-3 w-3 mr-2" />
                        Refresh Data
                      </button>
                      <button
                        onClick={() => window.open(`https://ipinfo.io/${selectedIp}`, '_blank')}
                        className="px-3 py-1 bg-blue-900/40 hover:bg-blue-800/40 text-blue-300 rounded-md text-sm flex items-center"
                      >
                        <Search className="h-3 w-3 mr-2" />
                        View on ipinfo.io
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      
      {/* Detail view for log entry */}
      {detailsOpen && selectedLog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-background-dark border border-border-dark/50 rounded-lg shadow-lg w-full max-w-4xl mx-auto overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between bg-gradient-to-r from-indigo-900/30 to-violet-900/30 border-b border-indigo-800/30 p-4 sticky top-0 z-10">
              <h3 className="text-xl font-medium flex items-center">
                <AuditLogTypeIcon type={selectedLog.event_type} />
                <span className="ml-2">Event Details</span>
                <Badge variant="primary" className="ml-3">{selectedLog.event_type}</Badge>
              </h3>
              <button 
                onClick={closeLogDetails}
                className="p-1 rounded-full hover:bg-background-dark/40 text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1">
              <div className="p-6 space-y-6">
                {/* Tabs for Details and Request/Response Data */}
                <div className="border-b border-border-dark">
                  <div className="flex -mb-px">
                    <button 
                      onClick={() => setActiveDetailTab('details')}
                      className={`px-4 py-2 border-b-2 font-medium flex items-center ${
                        activeDetailTab === 'details' 
                          ? 'border-primary text-primary' 
                          : 'border-transparent text-gray-400 hover:text-gray-300'
                      }`}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Basic Details
                    </button>
                    <button 
                      onClick={() => setActiveDetailTab('request')}
                      className={`px-4 py-2 border-b-2 font-medium flex items-center ${
                        activeDetailTab === 'request' 
                          ? 'border-primary text-primary' 
                          : 'border-transparent text-gray-400 hover:text-gray-300'
                      }`}
                    >
                      <ArrowUpDown className="h-4 w-4 mr-2" />
                      Request/Response Data
                    </button>
                  </div>
                </div>
                
                {activeDetailTab === 'details' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      {/* Basic Information */}
                      <div>
                        <h4 className="text-lg font-medium mb-4 text-gray-200 flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-indigo-400" />
                          Basic Information
                        </h4>
                        <div className="space-y-3 bg-background-dark/40 rounded-lg border border-indigo-900/20 p-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="bg-background-dark/60 p-3 rounded-md">
                              <div className="text-xs text-gray-400 mb-1 flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                Time (UTC)
                              </div>
                              <div className="font-mono text-sm">{selectedLog.timestamp}</div>
                            </div>
                            <div className="bg-background-dark/60 p-3 rounded-md">
                              <div className="text-xs text-gray-400 mb-1 flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                Time (Local)
                              </div>
                              <div className="font-mono text-sm">{formatTimestamp(selectedLog.timestamp)}</div>
                            </div>
                          </div>
                          
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1 flex items-center">
                              <Layers className="h-3 w-3 mr-1" />
                              Event Type
                            </div>
                            <div className="flex items-center">
                              <AuditLogTypeIcon type={selectedLog.event_type} />
                              <Badge variant="primary" className="ml-2">{selectedLog.event_type}</Badge>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="bg-background-dark/60 p-3 rounded-md">
                              <div className="text-xs text-gray-400 mb-1 flex items-center">
                                <Zap className="h-3 w-3 mr-1" />
                                Response Time
                              </div>
                              <div className={`flex items-center ${
                                selectedLog.response_time && selectedLog.response_time > 1000 ? 'text-rose-400' :
                                selectedLog.response_time && selectedLog.response_time > 500 ? 'text-amber-400' :
                                'text-emerald-400'
                              }`}>
                                {selectedLog.response_time ? (
                                  <>
                                    <span className="text-lg font-mono font-medium">{selectedLog.response_time}</span>
                                    <span className="text-xs ml-1">ms</span>
                                  </>
                                ) : 'N/A'}
                              </div>
                            </div>
                            <div className="bg-background-dark/60 p-3 rounded-md">
                              <div className="text-xs text-gray-400 mb-1 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Status Code
                              </div>
                              <div>
                                <Badge variant={getStatusBadgeColor(selectedLog.status_code)} className="text-lg">
                                  {selectedLog.status_code || 'N/A'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Request Information */}
                      <div>
                        <h4 className="text-lg font-medium mb-4 text-gray-200 flex items-center">
                          <ArrowUpDown className="h-4 w-4 mr-2 text-indigo-400" />
                          Request Information
                        </h4>
                        <div className="space-y-3 bg-background-dark/40 rounded-lg border border-indigo-900/20 p-4">
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1 flex items-center">
                              <FileText className="h-3 w-3 mr-1" />
                              Endpoint
                            </div>
                            <div className="font-mono text-sm break-all">{selectedLog.endpoint || 'N/A'}</div>
                          </div>
                          
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1 flex items-center">
                              <ArrowUpDown className="h-3 w-3 mr-1" />
                              HTTP Method
                            </div>
                            <div>
                              <MethodBadge method={selectedLog.method || ''} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      {/* User Information */}
                      <div>
                        <h4 className="text-lg font-medium mb-4 text-gray-200 flex items-center">
                          <User className="h-4 w-4 mr-2 text-indigo-400" />
                          User Information
                        </h4>
                        <div className="space-y-3 bg-background-dark/40 rounded-lg border border-indigo-900/20 p-4">
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1">User</div>
                            {selectedLog.username ? (
                              <div className="flex items-center">
                                <div className="w-8 h-8 rounded-full bg-indigo-800/60 flex items-center justify-center text-sm font-medium mr-2">
                                  {selectedLog.username[0].toUpperCase()}
                                </div>
                                <div>
                                  <div className="text-sm">{selectedLog.username}</div>
                                  {selectedLog.user_id && (
                                    <div className="text-xs text-gray-400">ID: {selectedLog.user_id}</div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="text-gray-400">Not authenticated</div>
                            )}
                          </div>
                          
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1">Tenant</div>
                            <div className="text-sm">
                              {selectedLog.tenant_id ? selectedLog.tenant_id : 'N/A'}
                            </div>
                          </div>
                          
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1">Session ID</div>
                            <div className="font-mono text-xs break-all">
                              {selectedLog.session_id ? selectedLog.session_id : 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Client Information */}
                      <div>
                        <h4 className="text-lg font-medium mb-4 text-gray-200 flex items-center">
                          <Globe className="h-4 w-4 mr-2 text-indigo-400" />
                          Client Information
                        </h4>
                        <div className="space-y-3 bg-background-dark/40 rounded-lg border border-indigo-900/20 p-4">
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1 flex items-center">
                              <Globe className="h-3 w-3 mr-1" />
                              IP Address
                            </div>
                            {selectedLog.ip_address ? (
                              <div className="flex items-center">
                                <span className="inline-block h-2 w-2 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
                                <span className="font-mono text-sm">{selectedLog.ip_address}</span>
                                {selectedLog.ip_address && !selectedLog.ip_address.startsWith('127.') && (
                                  <button 
                                    onClick={() => openIPDetails(selectedLog.ip_address!)}
                                    className="ml-2 p-1 rounded-full hover:bg-indigo-900/40 text-gray-400 hover:text-indigo-300 transition-colors"
                                    title="View IP details"
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="text-gray-400">N/A</div>
                            )}
                            
                            {/* IP Location - only if we have this data */}
                            {selectedLog.ip_address && geoCache[selectedLog.ip_address] && !geoCache[selectedLog.ip_address].error && !geoCache[selectedLog.ip_address].loading && (
                              <div className="mt-2 text-xs text-gray-400">
                                <div className="flex items-center mb-1">
                                  <Globe className="h-3 w-3 mr-1 text-blue-400" />
                                  Location:
                                </div>
                                <div className="pl-4">
                                  {geoCache[selectedLog.ip_address].city && (
                                    <div>City: {geoCache[selectedLog.ip_address].city}</div>
                                  )}
                                  {geoCache[selectedLog.ip_address].region && (
                                    <div>Region: {geoCache[selectedLog.ip_address].region}</div>
                                  )}
                                  {geoCache[selectedLog.ip_address].country && (
                                    <div>Country: {geoCache[selectedLog.ip_address].country}</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1">User Agent</div>
                            <div className="text-xs font-mono break-all">
                              {selectedLog.user_agent || 'N/A'}
                            </div>
                          </div>
                          
                          <div className="bg-background-dark/60 p-3 rounded-md">
                            <div className="text-xs text-gray-400 mb-1">Referrer</div>
                            <div className="text-xs font-mono break-all">
                              {selectedLog.referrer || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeDetailTab === 'request' && (
                  <div className="space-y-6">
                    {/* Error message if present */}
                    {selectedLog.error_message && (
                      <div className="bg-rose-900/20 border border-rose-800/30 rounded-lg p-4">
                        <h4 className="text-lg font-medium mb-2 text-rose-300 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Error Information
                        </h4>
                        <div className="font-mono text-sm text-rose-200 bg-rose-950/30 p-3 rounded-md">
                          {selectedLog.error_message}
                        </div>
                      </div>
                    )}
                    
                    {/* Request data */}
                    <div>
                      <h4 className="text-lg font-medium mb-2 text-gray-200 flex items-center">
                        <ArrowUpDown className="h-4 w-4 mr-2 text-indigo-400" />
                        Request Data
                      </h4>
                      <div className="bg-background-dark/40 rounded-lg border border-indigo-900/20 p-4">
                        {selectedLog.request_data && Object.keys(selectedLog.request_data).length > 0 ? (
                          <div className="relative">
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(selectedLog.request_data, null, 2));
                                toast.success('Request data copied to clipboard');
                              }}
                              className="absolute top-2 right-2 p-1.5 rounded-md bg-background-dark/60 hover:bg-background-dark text-gray-400 hover:text-white transition-colors"
                              title="Copy to clipboard"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard">
                                <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                              </svg>
                            </button>
                            <pre className="font-mono text-xs text-gray-300 bg-background-dark/60 p-3 rounded-md overflow-x-auto whitespace-pre-wrap"
                              dangerouslySetInnerHTML={{
                                __html: formatJsonWithHighlighting(selectedLog.request_data)
                              }}
                            />
                          </div>
                        ) : (
                          <div className="text-gray-400 text-center py-4">No request data available</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Response data */}
                    <div>
                      <h4 className="text-lg font-medium mb-2 text-gray-200 flex items-center">
                        <ArrowUpDown className="h-4 w-4 mr-2 text-indigo-400" />
                        Response Data
                        {selectedLog.status_code && (selectedLog.status_code >= 400) && (
                          <Badge variant="danger" className="ml-2">Error Response</Badge>
                        )}
                      </h4>
                      <div className="bg-background-dark/40 rounded-lg border border-indigo-900/20 p-4">
                        {selectedLog.response_data && Object.keys(selectedLog.response_data).length > 0 ? (
                          <div className="relative">
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(selectedLog.response_data, null, 2));
                                toast.success('Response data copied to clipboard');
                              }}
                              className="absolute top-2 right-2 p-1.5 rounded-md bg-background-dark/60 hover:bg-background-dark text-gray-400 hover:text-white transition-colors"
                              title="Copy to clipboard"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard">
                                <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                              </svg>
                            </button>
                            <pre 
                              className={`font-mono text-xs text-gray-300 bg-background-dark/60 p-3 rounded-md overflow-x-auto whitespace-pre-wrap ${
                                selectedLog.status_code && selectedLog.status_code >= 400 ? 'border border-rose-900/50' : ''
                              }`}
                              dangerouslySetInnerHTML={{
                                __html: formatJsonWithHighlighting(
                                  selectedLog.response_data, 
                                  selectedLog.status_code ? selectedLog.status_code >= 400 : false
                                )
                              }}
                            />
                          </div>
                        ) : (
                          <div className="text-gray-400 text-center py-4">No response data available</div>
                        )}
                      </div>
                    </div>
                    
                    {/* Field Extractor */}
                    {selectedLog.response_data && Object.keys(selectedLog.response_data).length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-lg font-medium mb-2 text-gray-200 flex items-center">
                          <Search className="h-4 w-4 mr-2 text-indigo-400" />
                          Inspect Field
                        </h4>
                        <div className="bg-background-dark/40 rounded-lg border border-indigo-900/20 p-4">
                          <div className="flex gap-2 mb-3">
                            <input 
                              type="text" 
                              placeholder="Enter field path (e.g. data.user.id)" 
                              value={selectedField || ''}
                              onChange={(e) => setSelectedField(e.target.value)}
                              className="flex-1 px-3 py-2 bg-background-dark/60 border border-indigo-800/30 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
                            />
                            <button
                              onClick={() => {
                                if (selectedField) {
                                  try {
                                    // Extract the value using the path
                                    const path = selectedField.split('.');
                                    let value = {...selectedLog.response_data};
                                    
                                    for (const key of path) {
                                      if (value && typeof value === 'object' && key in value) {
                                        value = value[key];
                                      } else {
                                        throw new Error(`Path ${selectedField} not found in response`);
                                      }
                                    }
                                    
                                    setExtractedValue(value);
                                    toast.success(`Value extracted from ${selectedField}`);
                                  } catch (error) {
                                    console.error(error);
                                    toast.error('Field not found in response data');
                                    setExtractedValue(null);
                                  }
                                }
                              }}
                              className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
                            >
                              Extract
                            </button>
                          </div>
                          
                          {extractedValue !== null && (
                            <div className="mt-2 border-t border-indigo-800/20 pt-3">
                              <div className="flex justify-between items-center mb-2">
                                <div className="text-sm font-medium text-gray-300">
                                  Value from <span className="font-mono text-blue-400">{selectedField}</span>:
                                </div>
                                <button
                                  onClick={() => {
                                    const textValue = typeof extractedValue === 'object' 
                                      ? JSON.stringify(extractedValue, null, 2)
                                      : String(extractedValue);
                                    navigator.clipboard.writeText(textValue);
                                    toast.success('Value copied to clipboard');
                                  }}
                                  className="p-1 rounded-md bg-background-dark/60 hover:bg-background-dark text-gray-400 hover:text-white transition-colors"
                                  title="Copy to clipboard"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard">
                                    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                                  </svg>
                                </button>
                              </div>
                              {typeof extractedValue === 'object' ? (
                                <pre className="font-mono text-xs text-gray-300 bg-background-dark/60 p-3 rounded-md overflow-x-auto whitespace-pre-wrap"
                                  dangerouslySetInnerHTML={{
                                    __html: formatJsonWithHighlighting(extractedValue)
                                  }}
                                />
                              ) : (
                                <div className="font-mono text-sm text-white bg-background-dark/60 p-3 rounded-md overflow-x-auto break-all">
                                  {typeof extractedValue === 'string' 
                                    ? `"${extractedValue}"` 
                                    : String(extractedValue)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-indigo-900/10 to-violet-900/10 border-t border-indigo-800/30 p-4 flex justify-end">
              <Button onClick={closeLogDetails} className="bg-indigo-600 hover:bg-indigo-700 text-white border-none">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Quick Response View Modal */}
      {responseModalOpen && selectedLog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-background-dark border border-border-dark/50 rounded-lg shadow-lg w-full max-w-3xl mx-auto overflow-hidden max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between bg-gradient-to-r from-indigo-900/30 to-violet-900/30 border-b border-indigo-800/30 p-4 sticky top-0 z-10">
              <div className="flex items-center">
                <h3 className="text-xl font-medium flex items-center">
                  <ArrowUpDown className="h-5 w-5 mr-2 text-indigo-400" />
                  <span className="ml-1">API Response</span>
                </h3>
                <Badge 
                  variant={getStatusBadgeColor(selectedLog.status_code)} 
                  className="ml-3 text-lg"
                >
                  {selectedLog.status_code || 'N/A'}
                </Badge>
              </div>
              <button 
                onClick={closeResponseModal}
                className="p-1 rounded-full hover:bg-background-dark/40 text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 p-4">
              <div className="space-y-4">
                {/* Endpoint and method */}
                <div className="mb-4 bg-background-dark/60 p-3 rounded-md border border-indigo-900/20">
                  <div className="flex flex-wrap items-center gap-2">
                    <MethodBadge method={selectedLog.method || ''} />
                    <span className="font-mono text-sm text-gray-300">{selectedLog.endpoint || 'N/A'}</span>
                  </div>
                </div>
                
                {/* Error message if present */}
                {selectedLog.error_message && (
                  <div className="bg-rose-900/20 border border-rose-800/30 rounded-lg p-4">
                    <h4 className="text-md font-medium mb-2 text-rose-300 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Error Information
                    </h4>
                    <div className="font-mono text-sm text-rose-200 bg-rose-950/30 p-3 rounded-md">
                      {selectedLog.error_message}
                    </div>
                  </div>
                )}
                
                {/* Response data */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-md font-medium text-gray-200 flex items-center">
                      <ArrowUpDown className="h-4 w-4 mr-2 text-indigo-400" />
                      Response Data
                      {selectedLog.status_code && (selectedLog.status_code >= 400) && (
                        <Badge variant="danger" className="ml-2">Error Response</Badge>
                      )}
                    </h4>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (selectedLog.response_data) {
                            navigator.clipboard.writeText(JSON.stringify(selectedLog.response_data, null, 2));
                            toast.success('Response data copied to clipboard');
                          }
                        }}
                        className="p-1.5 rounded-md bg-background-dark/60 hover:bg-background-dark text-gray-400 hover:text-white transition-colors flex items-center gap-1 text-xs"
                        title="Copy to clipboard"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard">
                          <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                        </svg>
                        Copy
                      </button>
                      
                      <button
                        onClick={() => {
                          setDetailsOpen(true);
                          setActiveDetailTab('request');
                          setResponseModalOpen(false);
                        }}
                        className="p-1.5 rounded-md bg-indigo-900/60 hover:bg-indigo-800/80 text-indigo-300 hover:text-indigo-200 transition-colors flex items-center gap-1 text-xs"
                        title="View full details"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        Full Details
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-background-dark/40 rounded-lg border border-indigo-900/20 p-0">
                    {selectedLog.response_data && Object.keys(selectedLog.response_data).length > 0 ? (
                      <pre 
                        className={`font-mono text-xs text-gray-300 bg-background-dark/60 p-3 rounded-md overflow-x-auto whitespace-pre-wrap ${
                          selectedLog.status_code && selectedLog.status_code >= 400 ? 'border border-rose-900/50' : ''
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: formatJsonWithHighlighting(
                            selectedLog.response_data, 
                            selectedLog.status_code ? selectedLog.status_code >= 400 : false
                          )
                        }}
                      />
                    ) : (
                      <div className="text-gray-400 text-center py-4">No response data available</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-indigo-900/10 to-violet-900/10 border-t border-indigo-800/30 p-3 flex justify-end">
              <Button 
                onClick={() => {
                  setDetailsOpen(true);
                  setActiveDetailTab('request');
                  setResponseModalOpen(false);
                }} 
                variant="outline"
                className="mr-2 bg-background-dark/50 border-indigo-800/30 hover:bg-background-dark/70"
              >
                View Full Details
              </Button>
              <Button onClick={closeResponseModal} className="bg-indigo-600 hover:bg-indigo-700 text-white border-none">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
};

export default AuditLogPage;